import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import { useAgencyInfo } from "../../../../context/agency";
import { useAppServices } from "../../../../hook/services";
import env from "../../../../config";
import { toast } from "react-toastify";

const STORAGE_PREFIX = "customizer:onboarding";

const OnboardingChecklist = () => {
  const [agency] = useAgencyInfo();
  const AppService = useAppServices();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const [open, setOpen] = useState(false);
  const [showJsCssGuide, setShowJsCssGuide] = useState(false);
  const [kbConfig, setKbConfig] = useState(null);
  const [jsCssDone, setJsCssDone] = useState(
    () =>
      localStorage.getItem(
        `${STORAGE_PREFIX}:jsCssInstalled:${companyId}`
      ) === "true"
  );
  const [autoOpened, setAutoOpened] = useState(false);
  const [minimized, setMinimized] = useState(
    () =>
      localStorage.getItem(`${STORAGE_PREFIX}:minimized:${companyId}`) === "true"
  );

  const toggleMinimized = (e) => {
    e.stopPropagation();
    const next = !minimized;
    setMinimized(next);
    localStorage.setItem(
      `${STORAGE_PREFIX}:minimized:${companyId}`,
      next ? "true" : "false"
    );
  };

  const loginCssLink = `@import url("https://script.crmgenix.com/webhook/login_page_css/${companyId}");`;
  const returnCssLink = `@import url("https://script.crmgenix.com/webhook/retrun_css/${companyId}");`;
  const jsScriptLink = `<script src="${env.API_URL}/webhook/script/{{user.companyId}}/{{user.role}}/{{user.type}}"></script>`;

  const copySingle = (text, label) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    toast.success(`${label} copied`);
  };

  const fetchKbConfig = async () => {
    try {
      const { response } = await AppService.knowledgebase_customization.filter({
        query: `companyId=${companyId}`,
      });
      setKbConfig(response?.data || {});
    } catch (e) {
      setKbConfig({});
    }
  };

  useEffect(() => {
    fetchKbConfig();
  }, [companyId]);

  useEffect(() => {
    if (open) fetchKbConfig();
  }, [open]);

  const handleJsCssToggle = (e) => {
    e.stopPropagation();
    const next = !jsCssDone;
    setJsCssDone(next);
    localStorage.setItem(
      `${STORAGE_PREFIX}:jsCssInstalled:${companyId}`,
      next ? "true" : "false"
    );
  };

  const items = useMemo(() => {
    const supportConfigDone = !!agency?.support_configuration?.customMenuLinkId;
    const kbCustomMenuDone = !!kbConfig?.customMenuLinkId;
    const kbDomainDone =
      !!kbConfig?.domain && String(kbConfig.domain).trim().length > 0;

    return [
      {
        key: "modules",
        required: true,
        title: "Connect modules account",
        description:
          "Authorize GoHighLevel for Location Creator, Location Initiator, and Custom Value Form.",
        icon: "mdi:link-variant",
        accent: "#6366f1",
        done: !!agency?.modulesAuth,
        action: () => {
          setOpen(false);
          navigate(`/${companyId}/location-creator`);
        },
        actionLabel: "Connect now",
      },
      {
        key: "jsCss",
        required: true,
        title: "Install custom JS & CSS",
        description:
          "Paste the provided snippets into GoHighLevel → White Label.",
        icon: "mdi:code-tags",
        accent: "#0ea5e9",
        done: jsCssDone,
        action: () => setShowJsCssGuide(true),
        actionLabel: "Open setup guide",
        manualToggle: true,
      },
      {
        key: "support",
        required: true,
        title: "Connect support location",
        description:
          "Authorize a sub-account location so it can send and manage tickets.",
        icon: "mdi:headphones",
        accent: "#10b981",
        done: !!agency?.supportAuth,
        action: () => {
          setOpen(false);
          navigate(`/${companyId}/support`);
        },
        actionLabel: "Connect support",
      },
      {
        key: "supportCustomPage",
        required: false,
        title: "Add support custom page",
        description:
          "Show a custom menu link so locations can open support from inside GHL.",
        icon: "mdi:page-layout-body",
        accent: "#a855f7",
        done: supportConfigDone,
        action: () => {
          setOpen(false);
          navigate(`/${companyId}/support/configuration`);
        },
        actionLabel: "Configure",
      },
      {
        key: "kbCustomPage",
        required: false,
        title: "Add knowledgebase custom page",
        description:
          "Show a custom menu link so locations can browse the knowledgebase from inside GHL.",
        icon: "mdi:book-open-page-variant",
        accent: "#f59e0b",
        done: kbCustomMenuDone,
        action: () => {
          setOpen(false);
          navigate(`/${companyId}/knowledgebase/customization`);
        },
        actionLabel: "Configure",
      },
      {
        key: "kbDomain",
        required: false,
        title: "Set knowledgebase white-label domain",
        description:
          "Serve the knowledgebase from your own branded domain.",
        icon: "mdi:web",
        accent: "#ec4899",
        done: kbDomainDone,
        action: () => {
          setOpen(false);
          navigate(`/${companyId}/knowledgebase/customization`);
        },
        actionLabel: "Set domain",
      },
    ];
  }, [agency, kbConfig, jsCssDone, companyId]);

  const required = items.filter((i) => i.required);
  const optional = items.filter((i) => !i.required);
  const requiredDone = required.filter((i) => i.done).length;
  const totalDone = items.filter((i) => i.done).length;
  const totalSteps = items.length;
  const requiredPct = Math.round((requiredDone / required.length) * 100);
  const allRequiredDone = requiredDone === required.length;
  const allDone = totalDone === totalSteps;
  const remaining = totalSteps - totalDone;

  // Auto-open the first time per company if any required step is missing.
  useEffect(() => {
    if (autoOpened) return;
    if (kbConfig === null) return; // wait for kb fetch
    const seen = localStorage.getItem(
      `${STORAGE_PREFIX}:autoOpened:${companyId}`
    );
    if (!seen && !allRequiredDone) {
      setOpen(true);
      localStorage.setItem(`${STORAGE_PREFIX}:autoOpened:${companyId}`, "true");
    }
    setAutoOpened(true);
  }, [autoOpened, kbConfig, allRequiredDone, companyId]);

  return (
    <>
      <style>{`
        .ob-launcher-wrap {
          position: fixed;
          right: 20px;
          bottom: 20px;
          z-index: 1500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .ob-launcher {
          position: relative;
          width: 52px;
          height: 52px;
          padding: 0;
          border-radius: 50%;
          border: none;
          color: #fff;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #ec4899 100%);
          box-shadow: 0 8px 22px rgba(99, 102, 241, 0.35);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .ob-launcher:hover {
          transform: translateY(-2px) scale(1.04);
          box-shadow: 0 14px 30px rgba(99, 102, 241, 0.45);
        }
        .ob-launcher.minimized {
          width: 34px;
          height: 34px;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.28);
        }
        .ob-launcher .ob-launcher-icon {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .ob-launcher.minimized .ob-launcher-icon {
          width: 18px;
          height: 18px;
        }
        .ob-launcher .ob-launcher-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 999px;
          background: #fff;
          color: #4338ca;
          font-size: 11px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #fff;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        }
        .ob-launcher.minimized .ob-launcher-badge {
          display: none;
        }
        .ob-launcher.complete {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 8px 22px rgba(16, 185, 129, 0.3);
        }
        .ob-launcher.complete .ob-launcher-badge {
          color: #047857;
        }

        /* Tooltip */
        .ob-launcher::before {
          content: attr(data-label);
          position: absolute;
          right: calc(100% + 12px);
          top: 50%;
          transform: translateY(-50%) translateX(4px);
          background: #0f172a;
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease, transform 0.15s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .ob-launcher::after {
          content: "";
          position: absolute;
          right: calc(100% + 6px);
          top: 50%;
          transform: translateY(-50%);
          border: 6px solid transparent;
          border-left-color: #0f172a;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease;
        }
        .ob-launcher:hover::before {
          opacity: 1;
          transform: translateY(-50%) translateX(0);
        }
        .ob-launcher:hover::after { opacity: 1; }

        @keyframes ob-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(236, 72, 153, 0.4), 0 8px 22px rgba(99, 102, 241, 0.35); }
          100% { box-shadow: 0 0 0 14px rgba(236, 72, 153, 0), 0 8px 22px rgba(99, 102, 241, 0.35); }
        }
        .ob-launcher.has-required:not(.minimized) {
          animation: ob-pulse-ring 2.2s ease-out infinite;
        }

        /* Minimize/expand handle */
        .ob-minimize-toggle {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #fff;
          border: 1px solid #e5e7eb;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
          opacity: 0;
          transform: translateY(2px);
          transition: opacity 0.15s ease, transform 0.15s ease, color 0.15s ease, background 0.15s ease;
          padding: 0;
        }
        .ob-launcher-wrap:hover .ob-minimize-toggle,
        .ob-minimize-toggle:focus-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .ob-minimize-toggle:hover {
          color: #4f46e5;
          background: #eef2ff;
        }

        .ob-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(2px);
          z-index: 1600;
          opacity: 0;
          animation: ob-fade-in 0.2s ease forwards;
        }
        @keyframes ob-fade-in { to { opacity: 1; } }

        .ob-panel {
          position: fixed;
          top: 0;
          right: 0;
          height: 100vh;
          width: 460px;
          max-width: 100vw;
          background: #f8fafc;
          z-index: 1700;
          display: flex;
          flex-direction: column;
          box-shadow: -16px 0 40px rgba(15, 23, 42, 0.18);
          transform: translateX(100%);
          animation: ob-slide-in 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        @keyframes ob-slide-in { to { transform: translateX(0); } }

        .ob-panel-header {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #ec4899 100%);
          color: #fff;
          padding: 24px 24px 28px;
          position: relative;
          overflow: hidden;
        }
        .ob-panel-header::after {
          content: "";
          position: absolute;
          right: -60px;
          top: -60px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%);
          pointer-events: none;
        }
        .ob-panel-close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.25);
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s;
        }
        .ob-panel-close:hover { background: rgba(255,255,255,0.3); }

        .ob-progress-track {
          height: 8px;
          border-radius: 999px;
          background: rgba(255,255,255,0.22);
          margin-top: 14px;
          overflow: hidden;
        }
        .ob-progress-fill {
          height: 100%;
          background: #fff;
          border-radius: 999px;
          transition: width 0.4s ease;
        }

        .ob-panel-body {
          flex: 1;
          overflow-y: auto;
          padding: 18px 20px 28px;
        }

        .ob-section-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #6366f1;
          margin: 14px 4px 10px;
        }
        .ob-section-label.optional { color: #94a3b8; }

        .ob-item {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px;
          border-radius: 14px;
          background: #fff;
          border: 1px solid #e2e8f0;
          margin-bottom: 10px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .ob-item:hover {
          border-color: #c7d2fe;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
        }
        .ob-item.done {
          background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%);
          border-color: #bbf7d0;
        }
        .ob-item-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #fff;
          font-size: 18px;
        }
        .ob-item-body { flex: 1; min-width: 0; }
        .ob-item-title {
          font-weight: 700;
          font-size: 14px;
          color: #0f172a;
          margin: 0 0 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .ob-item-desc {
          font-size: 12.5px;
          color: #64748b;
          line-height: 1.5;
          margin: 0 0 10px;
        }
        .ob-item-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .ob-item-action {
          background: #4f46e5;
          color: #fff;
          border: none;
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .ob-item-action:hover { background: #4338ca; }
        .ob-item.done .ob-item-action {
          background: #ffffff;
          color: #0f172a;
          border: 1px solid #cbd5e1;
        }
        .ob-item.done .ob-item-action:hover { background: #f1f5f9; }
        .ob-mark-toggle {
          background: transparent;
          border: 1px solid #cbd5e1;
          color: #475569;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .ob-mark-toggle:hover { background: #f1f5f9; }
        .ob-mark-toggle.on {
          background: #dcfce7;
          color: #15803d;
          border-color: #86efac;
        }
        .ob-status-pill {
          font-size: 11px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 999px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .ob-status-pill.done { background: #dcfce7; color: #15803d; }
        .ob-status-pill.todo { background: #fef3c7; color: #b45309; }
        .ob-status-pill.optional { background: #e2e8f0; color: #475569; }

        .ob-success-banner {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border: 1px solid #6ee7b7;
          color: #065f46;
          padding: 14px 16px;
          border-radius: 12px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .jscss-modal .modal-content {
          border-radius: 14px;
          overflow: hidden;
        }
        .jscss-modal .step-pill {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 13px;
          flex-shrink: 0;
          background: #e0e7ff;
          color: #4338ca;
        }
        .jscss-modal .snippet-card {
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          background: #fff;
        }
        .jscss-modal .snippet-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 600;
        }
        .jscss-modal .snippet-head.css { background: #eff6ff; color: #1d4ed8; }
        .jscss-modal .snippet-head.js  { background: #fff7ed; color: #c2410c; }
        .jscss-modal .snippet-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-top: 1px solid #f1f5f9;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12.5px;
        }
        .jscss-modal .snippet-row code {
          flex: 1;
          color: #334155;
          word-break: break-all;
          background: transparent;
        }
        .jscss-modal .copy-btn {
          border: none;
          background: #f1f5f9;
          color: #334155;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }
        .jscss-modal .copy-btn:hover { background: #e2e8f0; }
        .jscss-modal .step-row {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        @media (max-width: 480px) {
          .ob-launcher-wrap { right: 12px; bottom: 12px; }
          .ob-launcher { width: 48px; height: 48px; }
          .ob-launcher.minimized { width: 30px; height: 30px; }
          .ob-panel { width: 100vw; }
        }
      `}</style>

      <div className="ob-launcher-wrap">
        <button
          type="button"
          className={`ob-launcher ${allDone ? "complete" : ""} ${
            !allRequiredDone ? "has-required" : ""
          } ${minimized ? "minimized" : ""}`}
          onClick={() => setOpen(true)}
          data-label={
            minimized
              ? "Open setup checklist"
              : allDone
              ? `All set! ${totalDone}/${totalSteps}`
              : `Setup checklist · ${totalDone}/${totalSteps}`
          }
          aria-label={allDone ? "Setup complete" : "Open setup checklist"}
        >
          <span className="ob-launcher-icon">
            <Icon
              icon={allDone ? "mdi:check-circle" : "mdi:rocket-launch-outline"}
              style={{ fontSize: minimized ? 14 : 22 }}
            />
          </span>
          {!minimized && (
            <span className="ob-launcher-badge">
              {totalDone}/{totalSteps}
            </span>
          )}
        </button>
        <button
          type="button"
          className="ob-minimize-toggle"
          onClick={toggleMinimized}
          aria-label={minimized ? "Expand onboarding button" : "Minimize onboarding button"}
          title={minimized ? "Expand" : "Minimize"}
        >
          <Icon icon={minimized ? "mdi:chevron-up" : "mdi:chevron-down"} style={{ fontSize: 14 }} />
        </button>
      </div>

      {open && (
        <>
          <div className="ob-overlay" onClick={() => setOpen(false)} />
          <aside className="ob-panel" role="dialog" aria-label="Setup checklist">
            <div className="ob-panel-header">
              <button
                type="button"
                className="ob-panel-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <Icon icon="mdi:close" />
              </button>
              <div
                className="d-inline-flex align-items-center gap-2"
                style={{
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                <Icon icon="mdi:rocket-launch-outline" />
                Get started
              </div>
              <h4 className="fw-bold mt-3 mb-1" style={{ fontSize: 20 }}>
                Finish setting up your workspace
              </h4>
              <p
                className="mb-0 small"
                style={{ color: "rgba(255,255,255,0.85)" }}
              >
                {allRequiredDone
                  ? "Required setup is complete. Add the optional touches to fully white-label your modules."
                  : `Complete ${
                      required.length - requiredDone
                    } more required step${
                      required.length - requiredDone === 1 ? "" : "s"
                    } to unlock everything.`}
              </p>
              <div
                className="d-flex justify-content-between align-items-center mt-3"
                style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}
              >
                <span>Required progress</span>
                <span className="fw-bold">
                  {requiredDone} of {required.length}
                </span>
              </div>
              <div className="ob-progress-track">
                <div
                  className="ob-progress-fill"
                  style={{ width: `${requiredPct}%` }}
                />
              </div>
            </div>

            <div className="ob-panel-body">
              {allDone && (
                <div className="ob-success-banner">
                  <Icon
                    icon="mdi:party-popper"
                    style={{ fontSize: 24, flexShrink: 0 }}
                  />
                  <div className="small">
                    <strong>Nice work!</strong> Every step on the list is done.
                  </div>
                </div>
              )}

              <div className="ob-section-label">
                <Icon icon="mdi:asterisk" /> Required
              </div>
              {required.map((item) => (
                <ChecklistItem
                  key={item.key}
                  item={item}
                  onJsCssToggle={handleJsCssToggle}
                />
              ))}

              <div className="ob-section-label optional">
                <Icon icon="mdi:star-outline" /> Optional polish
              </div>
              {optional.map((item) => (
                <ChecklistItem
                  key={item.key}
                  item={item}
                  onJsCssToggle={handleJsCssToggle}
                />
              ))}
            </div>
          </aside>
        </>
      )}

      {showJsCssGuide && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            zIndex: 2000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowJsCssGuide(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg jscss-modal">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title mb-1 fw-bold d-flex align-items-center gap-2">
                    <Icon
                      icon="mdi:code-tags"
                      style={{ fontSize: 22, color: "#4f46e5" }}
                    />
                    Install custom JS &amp; CSS
                  </h5>
                  <small className="text-secondary-light">
                    Paste these snippets into GoHighLevel&rsquo;s White Label
                    settings.
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowJsCssGuide(false)}
                ></button>
              </div>

              <div className="modal-body pt-3">
                <div className="mb-4">
                  <div className="step-row">
                    <span className="step-pill">1</span>
                    <div className="small">
                      Open GoHighLevel &nbsp;&rarr;&nbsp;{" "}
                      <strong>Settings</strong> &nbsp;&rarr;&nbsp;{" "}
                      <strong>Company</strong> &nbsp;&rarr;&nbsp;{" "}
                      <strong>White Label</strong>
                    </div>
                  </div>
                  <div className="step-row">
                    <span className="step-pill">2</span>
                    <div className="small">
                      Paste the snippets below into the matching{" "}
                      <strong>Custom CSS</strong> and{" "}
                      <strong>Custom JS</strong> fields.
                    </div>
                  </div>
                  <div className="step-row mb-0">
                    <span className="step-pill">3</span>
                    <div className="small">
                      Click <strong>Save Changes</strong> in each section, then
                      come back here and mark this step done.
                    </div>
                  </div>
                </div>

                <div className="snippet-card mb-3">
                  <div className="snippet-head css">
                    <span className="d-inline-flex align-items-center gap-2">
                      <Icon
                        icon="mdi:language-css3"
                        style={{ fontSize: 18 }}
                      />
                      Custom CSS field
                    </span>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() =>
                        copySingle(`${loginCssLink}\n${returnCssLink}`, "CSS")
                      }
                    >
                      <Icon icon="mdi:content-copy" /> Copy both
                    </button>
                  </div>
                  <div className="snippet-row">
                    <code>{loginCssLink}</code>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => copySingle(loginCssLink, "Login CSS")}
                      title="Copy"
                    >
                      <Icon icon="mdi:content-copy" />
                    </button>
                  </div>
                  <div className="snippet-row">
                    <code>{returnCssLink}</code>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => copySingle(returnCssLink, "Return CSS")}
                      title="Copy"
                    >
                      <Icon icon="mdi:content-copy" />
                    </button>
                  </div>
                </div>

                <div className="snippet-card">
                  <div className="snippet-head js">
                    <span className="d-inline-flex align-items-center gap-2">
                      <Icon
                        icon="mdi:language-javascript"
                        style={{ fontSize: 18 }}
                      />
                      Custom JS field
                    </span>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => copySingle(jsScriptLink, "JS")}
                    >
                      <Icon icon="mdi:content-copy" /> Copy
                    </button>
                  </div>
                  <div className="snippet-row">
                    <code>{jsScriptLink}</code>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => copySingle(jsScriptLink, "JS")}
                      title="Copy"
                    >
                      <Icon icon="mdi:content-copy" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 d-flex justify-content-between">
                <button
                  type="button"
                  className={`ob-mark-toggle ${jsCssDone ? "on" : ""}`}
                  onClick={handleJsCssToggle}
                >
                  <Icon
                    icon={
                      jsCssDone
                        ? "mdi:check-circle"
                        : "mdi:checkbox-blank-circle-outline"
                    }
                  />
                  {jsCssDone ? "Marked as done" : "Mark as done"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={() => setShowJsCssGuide(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ChecklistItem = ({ item, onJsCssToggle }) => {
  return (
    <div className={`ob-item ${item.done ? "done" : ""}`}>
      <div
        className="ob-item-icon"
        style={{
          background: item.done
            ? "linear-gradient(135deg, #10b981, #059669)"
            : `linear-gradient(135deg, ${item.accent}, ${item.accent}dd)`,
        }}
      >
        <Icon icon={item.done ? "mdi:check" : item.icon} />
      </div>
      <div className="ob-item-body">
        <h6 className="ob-item-title">
          {item.title}
          {item.done ? (
            <span className="ob-status-pill done">Done</span>
          ) : item.required ? (
            <span className="ob-status-pill todo">Required</span>
          ) : (
            <span className="ob-status-pill optional">Optional</span>
          )}
        </h6>
        <p className="ob-item-desc">{item.description}</p>
        <div className="ob-item-actions">
          <button
            type="button"
            className="ob-item-action"
            onClick={item.action}
          >
            {item.done ? (
              <>
                <Icon icon="mdi:eye-outline" />
                Review
              </>
            ) : (
              <>
                <Icon icon="mdi:arrow-right-thin" />
                {item.actionLabel}
              </>
            )}
          </button>
          {item.manualToggle && (
            <button
              type="button"
              className={`ob-mark-toggle ${item.done ? "on" : ""}`}
              onClick={onJsCssToggle}
              title={
                item.done ? "Mark as not done" : "Mark this step as completed"
              }
            >
              <Icon
                icon={
                  item.done
                    ? "mdi:check-circle"
                    : "mdi:checkbox-blank-circle-outline"
                }
              />
              {item.done ? "Done" : "Mark done"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingChecklist;
